import { useEffect, useRef, useState, type ReactNode } from "react";

function reducedMotionQuery() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return null;
  return window.matchMedia("(prefers-reduced-motion: reduce)");
}

function prefersReducedMotion() {
  return reducedMotionQuery()?.matches ?? true;
}

/**
 * Delikatne pojawianie się sekcji przy przewijaniu (fade + lekkie uniesienie).
 * Respektuje preferencję ograniczonego ruchu i działa bezpiecznie przy SSR.
 */
export function Reveal({
  children,
  delay = 0,
  as: Tag = "div",
  className = "",
}: {
  children: ReactNode;
  delay?: number;
  as?: "div" | "section" | "li" | "figure";
  className?: string;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        }
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref as never}
      style={{ transitionDelay: shown ? `${delay}ms` : undefined }}
      className={`reveal ${shown ? "reveal-in" : ""} ${className}`}
    >
      {children}
    </Tag>
  );
}

/**
 * Bardzo lekki parallax dla zdjęć w sekcjach hero.
 * Siła jest skalowana do szerokości ekranu (na telefonie ~40%),
 * a przy „ograniczonym ruchu” efekt wyłącza się natychmiast (reaktywnie).
 */
export function useParallax(strength = 14) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let frame = 0;
    let active = false;

    const scaled = () => {
      const w = window.innerWidth;
      if (w < 480) return strength * 0.35;
      if (w < 768) return strength * 0.55;
      if (w < 1024) return strength * 0.8;
      return strength;
    };

    const update = () => {
      frame = 0;
      const rect = el.getBoundingClientRect();
      const progress = (rect.top + rect.height / 2) / window.innerHeight - 0.5;
      el.style.transform = `translate3d(0, ${(-progress * scaled()).toFixed(2)}px, 0)`;
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(update);
    };

    const enable = () => {
      if (active) return;
      active = true;
      el.style.willChange = "transform";
      update();
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll, { passive: true });
    };

    const disable = () => {
      if (!active) return;
      active = false;
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      el.style.transform = "";
      el.style.willChange = "";
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };

    const mql = reducedMotionQuery();
    const sync = () => (mql?.matches ? disable() : enable());
    sync();
    mql?.addEventListener("change", sync);

    return () => {
      disable();
      mql?.removeEventListener("change", sync);
    };
  }, [strength]);

  return ref;
}
